import classNames from 'classnames';
import * as React from 'react';
import { SubjectsBasket } from '../Basket/SubjectsBasket';
import { OnlineStudyPlanContext } from '../Context/OnlineStudyPlanContext';
import { GetSubjectsTotalPoints, GetSubjectsTotalPrice } from '../Context/OnlineStudyPlanHelpers';
import { Txt } from '../Txt';
import { Button } from '~/Partials/Button/Button';
import type { TabKey } from '~/Partials/TabList/Tab';
import { Tab } from '~/Partials/TabList/Tab';
import { TabList } from '~/Partials/TabList/TabList';
import { TabPanel } from '~/Partials/Tabs/TabPanel';
import { moneyToString } from '~/Partials/Utils/utils';
import { ScrollLock } from '~/Utils/ScrollLock/ScrollLock';
import { useSubjectsTabModel } from './SubjectsTabModel';
import styles from './SubjectsTabs.module.css';

export interface SubjectsTabsTabProps {
    tabName: string;
}

export interface SubjectsTabsParentProps {
    tabsLabel: string;
    children: (React.ReactElement<SubjectsTabsTabProps> | boolean)[];
}

export const SubjectsTabs: React.FC<React.PropsWithChildren<SubjectsTabsParentProps>> = props => {
    const { tabsLabel, children } = props;
    const { currentTab, setCurrentTab, getText, choicesFromBasket } = React.useContext(OnlineStudyPlanContext);

    const model = useSubjectsTabModel();

    const selectedSubjects = choicesFromBasket.map(i => i.subject);
    const totalPoints = GetSubjectsTotalPoints(selectedSubjects);
    const totalPrice = GetSubjectsTotalPrice(selectedSubjects);

    const renderTabHeading = (tabKey: TabKey, tabName: string) => (
        <Tab
            key={tabKey}
            tabKey={tabKey}
            selected={tabKey === currentTab}
            onSelect={setCurrentTab}
            className={styles.Tab}
        >
            {tabName}
        </Tab>
    );

    const renderTabBody = (body: React.ReactElement<SubjectsTabsTabProps>, tabKey: TabKey) => (
        <TabPanel key={tabKey} tabKey={tabKey} className={classNames(tabKey !== currentTab && styles['Tab--inactive'])}>
            {body}
        </TabPanel>
    );

    const childrenTabs = (children ? children.filter(c => !!c) : []) as React.ReactElement<SubjectsTabsTabProps>[];

    return (
        <div>
            <div className={styles.Header}>
                <h2 className={styles.Subtitle}>
                    <Txt id="studyPlan" />
                </h2>
                <TabList name={tabsLabel} className={styles.TabsBar}>
                    {childrenTabs.map(child => renderTabHeading(child.props.tabName, child.props.tabName))}
                </TabList>
            </div>
            <div className={styles.Inner}>
                <div className={styles.TabBody}>{childrenTabs.map(tab => renderTabBody(tab, tab.props.tabName))}</div>

                <div className={styles.Basket}>
                    <ScrollLock isLocked={model.state.mobileBasketVisible} />
                    <Button
                        onClick={() => model.setMobileBasketVisibility(true)}
                        className={classNames(
                            !model.state.isBasketDisplayed && styles['Tab--inactive'],
                            styles.StickyBasket
                        )}
                    >
                        <span>{getText('overall')}</span>
                        <div>
                            <span className={styles.StickyBasketPoints}>
                                {totalPoints} {getText('points')}
                            </span>
                            <span>{moneyToString(totalPrice)}</span>
                        </div>
                    </Button>
                    <div
                        className={classNames(
                            styles.BasketSpace,
                            model.state.mobileBasketVisible && styles.MobileBasketVisible,
                            !model.state.isBasketDisplayed && styles['Tab--inactive']
                        )}
                    >
                        <div className={styles.BasketSpaceInner}>
                            <SubjectsBasket setMobileBasketVisibility={model.setMobileBasketVisibility} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
